import { useMemo } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { densityTrend, formatWeight, trainingCalendarMs } from "@heraion/shared";

import { ChartReadout } from "../charts/ChartHead";
import { useChartReadout } from "../charts/pointSelection";
import { WagmiLineChart } from "../charts/WagmiLineChart";
import { PanelTitleRow } from "./InfoPill";
import { panelDays, panelRange } from "./progressWindow";
import { makeTierTwoStyles } from "./tierTwoPanelStyles";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { useClockOn } from "../useClockOn";
import { weightUnitLabels } from "../weightUnitLabels";
import { isOfflineCode } from "../loadErrorText";
import { LoadErrorLine } from "./panelCards";
import { dateFormatter } from "@heraion/shared";
import { useDateLocale } from "../primitives/DateFormatContext";

// Session-density (kg/min) over the last 90 days.
export function DensityTrendPanel(deps: ProgressDeps) {
  const { theme, units } = deps;
  const { range } = deps;
  const windowDays = panelDays(range);
  const { t, i18n } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const { results, error } = useSessionResultsFromDeps(deps);
  const styles = useMemo(() => makeTierTwoStyles(theme), [theme]);
  const dateLocale = useDateLocale();
  const dateFmt = useMemo(
    () => dateFormatter(dateLocale, { day: "2-digit", month: "short" }),
    [dateLocale],
  );
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  const points = useMemo(() => {
    if (!results) return null;
    const r = panelRange(range);
    return densityTrend(results, r.toMs, panelDays(r));
  }, [results, range]);

  // HM-2 — density is an EVENT metric: the line may only break over a pause in
  // training, never over a session that simply wasn't dense enough to plot.
  // Every logged session counts here, not just the ones that produced a point.
  const trainingAtMs = useMemo(() => trainingCalendarMs(results), [results]);

  // PG-55: one clock per data load, not per render.
  const nowMs = useClockOn(points);

  const chartPoints = useMemo(
    () =>
      (points ?? []).map((p) => ({
        x: p.atMs,
        y: p.kgPerMin,
        label: dateFmt.format(new Date(p.atMs)),
      })),
    [points, dateFmt],
  );
  const yFormat = (n: number) => formatWeight(n, units, i18n.language, unitLabels);
  // PG-64 fetta 3: the header reads the tapped session, the latest by default.
  const { selection, readout, readoutProps } = useChartReadout(
    chartPoints,
    (n) => `${yFormat(n)}/min`,
    true,
  );

  const densityTitle = t("mobile.progress.density.title", {
    defaultValue: "Density",
  });
  const densityHelp = t("mobile.progress.density.help", {
    defaultValue:
      'Volume divided by session duration, kg/min. Reads as "how dense was that session": going up means the same load in less time (better conditioning) or more load in the same time (better strength). A sudden drop without changing the program often means fatigue: the rests stretched, planned or not.',
  });

  // Held numbers beat an error line: a failed refresh keeps them on screen.
  if (error && points === null) {
    return (
      <View style={[styles.card, isOfflineCode(error) ? null : styles.cardError]}>
        <PanelTitleRow
          theme={theme}
          title={densityTitle}
          body={densityHelp}
          period={period}
        />
        <LoadErrorLine theme={theme} error={error} />
      </View>
    );
  }

  if (points === null) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={densityTitle}
          body={densityHelp}
          period={period}
        />
        <ActivityIndicator color={theme.palette.brand500} />
      </View>
    );
  }

  if (points.length < 3) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={densityTitle}
          body={densityHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {t("mobile.progress.density.empty", {
            defaultValue: "Need 3+ timed sessions to chart density",
          })}
        </Text>
      </View>
    );
  }

  const latest = points[points.length - 1]!.kgPerMin;
  const first = points[0]!.kgPerMin;
  const deltaPct = first === 0 ? 0 : ((latest - first) / first) * 100;
  const trendIcon =
    deltaPct > 5 ? "trend-up" : deltaPct < -5 ? "trend-down" : "arrow-right";
  // The direction in words: the glyph alone says nothing to a screen reader.
  const trendLabel =
    deltaPct > 5
      ? t("mobile.progress.density.a11yUp", { defaultValue: "rising" })
      : deltaPct < -5
        ? t("mobile.progress.density.a11yDown", { defaultValue: "falling" })
        : t("mobile.progress.density.a11yFlat", { defaultValue: "flat" });
  // Trend arrow = advisory judgement (not a brand action): up→complete green,
  // down→ink2 (a drop is not an error), flat→ink3. Off the brand per S2.1.
  const arrowColor =
    deltaPct > 5
      ? theme.palette.complete
      : deltaPct < -5
        ? theme.palette.ink2
        : theme.palette.ink3;

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={densityTitle}
        body={densityHelp}
        period={period}
        // #388: no tap hint here. The coach's grid, this panel's only
        // screen, already shows it once, on the e1RM trends above.
        right={
          <ChartReadout
            theme={theme}
            readout={readout}
            readoutProps={readoutProps}
            trend={{ icon: trendIcon, color: arrowColor, label: trendLabel }}
          />
        }
      />
      <WagmiLineChart
        series={[
          {
            key: "density",
            variant: "primary",
            headline: true,
            family: "event",
            trainingAtMs,
            points: chartPoints,
          },
        ]}
        yFormat={yFormat}
        nowMs={nowMs}
        theme={theme}
        selection={selection}
      />
    </View>
  );
}
